import { useEffect, useMemo, useState } from 'react';
import { places } from './travelData';

function slugify(value) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function detectAspectFromDimensions(width, height) {
  const ratio = width / height;
  if (ratio >= 1.15) return 'wide';
  if (ratio <= 0.87) return 'portrait';
  return 'square';
}

function detectAspectFromFile(file) {
  return new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      resolve(detectAspectFromDimensions(image.width, image.height));
      URL.revokeObjectURL(objectUrl);
    };

    image.onerror = () => {
      resolve('wide');
      URL.revokeObjectURL(objectUrl);
    };

    image.src = objectUrl;
  });
}

const uploadControlsEnabled =
  process.env.NODE_ENV !== 'production' || process.env.REACT_APP_ENABLE_LOCAL_UPLOADS === 'true';

function PhotoDraftPanel({ coordinate, activePlaceId }) {
  const [open, setOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('photo');
  const [placeId, setPlaceId] = useState(places[0]?.id || '');
  const [placeName, setPlaceName] = useState('');
  const [placeZoom, setPlaceZoom] = useState('11');
  const [title, setTitle] = useState('');
  const [aspect, setAspect] = useState('wide');
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (activePlaceId) {
      setPlaceId(activePlaceId);
    }
  }, [activePlaceId]);

  const selectedPlace = places.find((place) => place.id === placeId) || places[0];

  const snippet = useMemo(() => {
    const cleanTitle = title || 'New Photo';
    const fileExtension = file?.name?.split('.').pop() || 'jpg';
    const fullPath = `/photos/full/${selectedPlace?.id || 'place'}/${slugify(cleanTitle)}.${fileExtension}`;
    const thumbPath = `/photos/thumbs/${selectedPlace?.id || 'place'}/${slugify(cleanTitle)}.jpg`;

    return JSON.stringify(
      {
        id: `${selectedPlace?.id || 'place'}-${slugify(cleanTitle) || 'new-photo'}`,
        title: cleanTitle,
        src: fullPath,
        thumbnail: thumbPath,
        longitude: coordinate?.longitude || 0,
        latitude: coordinate?.latitude || 0,
        aspect,
      },
      null,
      2
    );
  }, [aspect, coordinate, file, selectedPlace, title]);

  if (!uploadControlsEnabled) {
    return null;
  }

  const copySnippet = async () => {
    await navigator.clipboard?.writeText(snippet);
  };

  const handleFileChange = async (event) => {
    const nextFile = event.target.files?.[0] || null;
    setFile(nextFile);

    if (!nextFile) {
      return;
    }

    setStatus('Detecting image shape...');
    const nextAspect = await detectAspectFromFile(nextFile);
    setAspect(nextAspect);
    setStatus(`Detected as ${nextAspect}.`);
  };

  const reloadSoon = () => {
    window.setTimeout(() => window.location.reload(), 450);
  };

  const uploadPhoto = async () => {
    if (!coordinate) {
      setStatus('Klik eerst op de map voor coordinaten.');
      return;
    }

    if (!file) {
      setStatus('Kies eerst een foto.');
      return;
    }

    const formData = new FormData();
    formData.append('placeId', placeId);
    formData.append('title', title || file.name.replace(/\.[^.]+$/, ''));
    formData.append('longitude', coordinate.longitude);
    formData.append('latitude', coordinate.latitude);
    formData.append('aspect', aspect);
    formData.append('photo', file);

    setIsUploading(true);
    setStatus('Foto opslaan...');

    try {
      const response = await fetch('http://127.0.0.1:4321/photos', {
        method: 'POST',
        body: formData,
      });
      const result = await response.json();

      if (!response.ok || !result.ok) {
        throw new Error(result.error || 'Upload failed.');
      }

      setStatus('Opgeslagen.');
      setTitle('');
      setFile(null);
      reloadSoon();
    } catch (error) {
      setStatus(`Niet opgeslagen: ${error.message}. Draait npm run photo-server?`);
    } finally {
      setIsUploading(false);
    }
  };

  const createPlace = async () => {
    if (!coordinate) {
      setStatus('Klik eerst op de map voor coordinaten.');
      return;
    }

    if (!placeName.trim()) {
      setStatus('Geef de nieuwe plaats een naam.');
      return;
    }

    setIsUploading(true);
    setStatus('Plaats opslaan...');

    try {
      const response = await fetch('http://127.0.0.1:4321/places', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: placeName.trim(),
          longitude: coordinate.longitude,
          latitude: coordinate.latitude,
          zoom: Number(placeZoom) || 11,
        }),
      });
      const result = await response.json();

      if (!response.ok || !result.ok) {
        throw new Error(result.error || 'Place create failed.');
      }

      setStatus('Plaats opgeslagen.');
      setPlaceName('');
      setPlaceZoom('11');
      setPlaceId(result.place.id);
      reloadSoon();
    } catch (error) {
      setStatus(`Niet opgeslagen: ${error.message}. Draait npm run photo-server?`);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <aside className={`draft-panel ${open ? 'open' : ''}`}>
      <button className="draft-panel-toggle" type="button" onClick={() => setOpen((value) => !value)}>
        Add photo
      </button>

      {open && (
        <div className="draft-panel-body">
          <div className="draft-panel-tabs">
            <button
              type="button"
              className={activeSection === 'place' ? 'draft-panel-tab active' : 'draft-panel-tab'}
              onClick={() => setActiveSection('place')}
            >
              Add place
            </button>
            <button
              type="button"
              className={activeSection === 'photo' ? 'draft-panel-tab active' : 'draft-panel-tab'}
              onClick={() => setActiveSection('photo')}
            >
              Add photo
            </button>
          </div>

          {activeSection === 'place' && (
            <div className="draft-section">
              <label>
                Place name
                <input
                  value={placeName}
                  onChange={(event) => setPlaceName(event.target.value)}
                  placeholder="New place name"
                />
              </label>
              <label>
                Zoom
                <input
                  inputMode="decimal"
                  value={placeZoom}
                  onChange={(event) => setPlaceZoom(event.target.value)}
                  placeholder="11"
                />
              </label>
              <div className="draft-coordinates">
                {coordinate ? `${coordinate.latitude}, ${coordinate.longitude}` : 'Click map'}
              </div>
              <button type="button" onClick={createPlace} disabled={isUploading}>
                {isUploading ? 'Saving...' : 'Save place'}
              </button>
            </div>
          )}

          {activeSection === 'photo' && (
            <div className="draft-section">
              <label>
                Place
                <select value={placeId} onChange={(event) => setPlaceId(event.target.value)}>
                  {places.map((place) => (
                    <option key={place.id} value={place.id}>
                      {place.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Title
                <input
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  placeholder="Photo title"
                />
              </label>
              <label>
                Photo
                <input
                  accept="image/*"
                  type="file"
                  onChange={handleFileChange}
                />
              </label>
              <div className="draft-aspect">
                Shape: <b>{aspect}</b>
              </div>
              <div className="draft-coordinates">
                {coordinate ? `${coordinate.latitude}, ${coordinate.longitude}` : 'Click map'}
              </div>
              <textarea readOnly value={snippet} />
              <button type="button" onClick={uploadPhoto} disabled={isUploading}>
                {isUploading ? 'Saving...' : 'Save photo'}
              </button>
              <button type="button" onClick={copySnippet}>
                Copy JSON
              </button>
            </div>
          )}

          {status && <div className="draft-status">{status}</div>}
        </div>
      )}
    </aside>
  );
}

export default PhotoDraftPanel;
